import { useTranslation } from 'react-i18next';

import { FormHelperText } from '@mui/material';

import type { DateTimeRangeValue, DisplayMode } from 'Utils/dateTime';
import { getDateLimits } from 'Utils/dateTime';

import { DateTimePickerRange } from '@common/Form/DateTimePickerRange';
import { useDateTimeConfigFromConfigSlice } from '@hooks/useDateTimeConfigFromConfigSlice';
import { AddIcon, DeleteIcon } from '@/libs/shared/ui/icons';

import { convertCourseDateToDateTimeRange } from '../../../dates/course-dates-utils';

import {
  ActionButtonsContainer,
  CompactButton,
  CourseDateRow,
  DateTimePickerContainer,
  ErrorMessageContainer
} from '../styled';
import type { CourseDateEntry } from '../types';

type Props = {
  courseDate: CourseDateEntry;
  index: number;
  totalDates: number;
  userTimezone: string;
  courseTimezone?: string;
  courseDateErrors: Record<string, string>;
  onDateRangeChange: (id: string, dateTimeRange: DateTimeRangeValue) => void;
  onRemoveDate: (id: string) => void;
  onAddDate: () => void;
  onDateRangeBlur?: () => void;
  displayMode?: DisplayMode;
  getCourseDateForDisplay?: (id: string) => CourseDateEntry | undefined;
};

export const CourseDateItem = ({
  courseDate,
  index,
  totalDates,
  userTimezone,
  courseTimezone,
  courseDateErrors,
  onDateRangeChange,
  onRemoveDate,
  onAddDate,
  onDateRangeBlur,
  getCourseDateForDisplay
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const { dateFormat, timeFormat } = useDateTimeConfigFromConfigSlice();
  const { minDate, maxDate } = getDateLimits();

  const handleDateRangeChange = (dateTimeRange: DateTimeRangeValue) => {
    onDateRangeChange(courseDate.id, dateTimeRange);
  };

  const currentCourseDate = getCourseDateForDisplay?.(courseDate.id) ?? courseDate;

  const convertedValue = convertCourseDateToDateTimeRange(
    currentCourseDate,
    courseTimezone ?? userTimezone,
    dateFormat,
    timeFormat
  );

  return (
    <CourseDateRow>
      <DateTimePickerContainer>
        <DateTimePickerRange
          value={convertedValue}
          onChange={handleDateRangeChange}
          onBlur={onDateRangeBlur}
          required
          minDate={minDate}
          maxDate={maxDate}
          startMinDate={minDate}
          startMaxDate={maxDate}
          endMinDate={convertedValue.startDate || minDate}
          endMaxDate={maxDate}
        />
        {courseDateErrors[courseDate.id] && (
          <ErrorMessageContainer>
            <FormHelperText error>{courseDateErrors[courseDate.id]}</FormHelperText>
          </ErrorMessageContainer>
        )}
      </DateTimePickerContainer>

      <ActionButtonsContainer>
        <CompactButton
          variant="outlined"
          startIcon={<DeleteIcon />}
          onClick={() => onRemoveDate(courseDate.id)}
          color="error"
          disabled={totalDates <= 1}
          style={{
            opacity: totalDates > 1 ? 1 : 0,
            pointerEvents: totalDates > 1 ? 'auto' : 'none'
          }}
        >
          {t('courseForm.removeDate')}
        </CompactButton>

        <CompactButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddDate}
          style={{ visibility: index === totalDates - 1 ? 'visible' : 'hidden' }}
        >
          {t('courseForm.moreDates')}
        </CompactButton>
      </ActionButtonsContainer>
    </CourseDateRow>
  );
};
